// §6.3 / §19.4 — the Windows dialog: **in-window**, and **OK then Cancel**.
//
// "In-window" is the whole difference from the house dialog, and it needs to know whose window it
// is — which is why `DialogProps` carries the asking app (D-109). A dialog the shell raised itself
// has no owner and falls back to the middle of the screen, which is also where one goes when the
// asking app has no window on this desktop.
//
// The focus trap is shell-owned and lives in `host/ShellUiHost`: a skin decides what a dialog
// looks like, never whether an app can escape one.
import { useEffect, useRef, useState } from 'react';
import type { DialogProps, Rect } from '@hangar/shared';
import { COPY } from '@/copy';
import { useKernel, useWindows } from '@/kernel/react';

export function Dialog({ request, appId, onAccept, onCancel }: DialogProps) {
  const kernel = useKernel();
  const windows = useWindows();
  const [value, setValue] = useState(request.defaultValue ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const acceptRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setValue(request.defaultValue ?? '');
    if (request.kind === 'prompt') inputRef.current?.focus();
    else acceptRef.current?.focus();
  }, [request.id, request.kind, request.defaultValue]);

  const dismissible = request.kind !== 'alert';

  // The asking app's topmost window on the desktop you are looking at.
  const activeDesktop = kernel.wm.getState().activeDesktopId;
  const owner: Rect | null =
    appId === null
      ? null
      : (windows
          .filter(
            (win) =>
              win.appId === appId && win.desktopId === activeDesktop && win.state !== 'minimized',
          )
          .sort((a, b) => b.z - a.z)[0]?.rect ?? null);

  return (
    <div
      className="win11-dialog"
      role="dialog"
      aria-modal="true"
      data-testid="shell-dialog"
      data-anchored={String(owner !== null)}
      style={
        owner
          ? {
              position: 'absolute',
              left: `${owner.x}px`,
              top: `${owner.y}px`,
              width: `${owner.w}px`,
              height: `${owner.h}px`,
            }
          : undefined
      }
    >
      <div className="win11-dialog-panel">
        <p className="win11-dialog-message" data-testid="shell-dialog-message">
          {request.message}
        </p>

        {request.kind === 'prompt' ? (
          <input
            ref={inputRef}
            type="text"
            className="win11-dialog-input"
            data-testid="shell-dialog-input"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onAccept(value);
            }}
          />
        ) : null}

        {/* §6.3 — OK first. It is the one thing about a Windows dialog everybody can name. */}
        <div className="win11-dialog-actions">
          <button
            ref={acceptRef}
            type="button"
            className="win11-dialog-button win11-dialog-button--primary"
            data-testid="shell-dialog-accept"
            onClick={() => onAccept(request.kind === 'prompt' ? value : undefined)}
          >
            {request.confirmLabel ?? COPY.chrome.ok}
          </button>
          {dismissible ? (
            <button
              type="button"
              className="win11-dialog-button"
              data-testid="shell-dialog-cancel"
              onClick={onCancel}
            >
              {request.cancelLabel ?? COPY.chrome.cancel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
